import { useState } from 'react';

import styled from '@emotion/styled';
import { colors } from '@modernanimal/common';
import PlacesInputField from '@modernanimal/common/src/web/PlacesInputField';
import useBreakpoint from 'use-breakpoint';

import { CustomerPatientRecordsAvailabilityEnum } from '../__generated__/graphql';
import PrimaryButton from '../components/PrimaryButton';
import Fonts from '../fonts';
import { BREAKPOINTS } from '../helpers';
import useWebBookingStore, { ExternalClinicStoreType, initialExternalClinicState } from '../store';

type Props = {
  goToNextStep: ()=> void;
};

const MedicalRecordsClinicRequest = ({ goToNextStep }: Props) => {
    const {
        patient, updatePatient, externalClinic, updateExternalClinic
    } = useWebBookingStore((state) => ({
        patient: state.patient,
        updatePatient: state.setPatient,
        externalClinic: state.externalClinic,
        updateExternalClinic: state.setExternalClinic,
    }));

    const [city, setCity] = useState<Pick<google.maps.places.PlacePrediction, 'text' | 'placeId'> | null>(
        (externalClinic.cityText && externalClinic.cityPlaceId)
            ? { text: externalClinic.cityText, placeId: externalClinic.cityPlaceId }
            : null
    );
    const [clinic, setClinic] = useState<Pick<google.maps.places.Place, 'displayName' | 'formattedAddress' | 'id' | 'nationalPhoneNumber'> | null>(
        externalClinic.placeId ? {
            displayName: externalClinic.name,
            formattedAddress: externalClinic.formattedAddress,
            id: externalClinic.placeId,
            nationalPhoneNumber: externalClinic.phoneNumber
        } : null
    );

    const { breakpoint } = useBreakpoint(BREAKPOINTS, 'desktop');
    const isMobile = breakpoint === 'mobile';
    const isValidInfo = clinic && city;

    const requestRecords = () => {
        const updatedExternalClinic: ExternalClinicStoreType = clinic
            ? {
                ...externalClinic,
                name: clinic.displayName ?? '',
                formattedAddress: clinic.formattedAddress ?? '',
                placeId: clinic.id ?? '',
                phoneNumber: clinic.nationalPhoneNumber ?? '',
                cityText: city?.text || {
                    matches: [],
                    text: '',
                },
                cityPlaceId: city?.placeId || '',
            }
            : initialExternalClinicState;

        updateExternalClinic({
            ...updatedExternalClinic,
        });

        updatePatient({
            ...patient,
            recordsAvailability: CustomerPatientRecordsAvailabilityEnum.RecordsNeeded,
        });

        goToNextStep();
    };

    const renderCityAndClinicInputs = () => (
        <Subbody>
            <PlacesInputField
                placeholder="City"
                hasChanged={externalClinic.cityPlaceId !== city?.placeId}
                showError={false}
                selectedValue={city}
                types={['(cities)']}
                onSelectResult={setCity}
                autoFocus={!city?.text.text && !city?.placeId}
            />
            <PlacesInputField
                searchType="textsearch"
                placeholder="Enter clinic name"
                hasChanged={externalClinic.placeId !== clinic?.id}
                showError={false}
                selectedValue={clinic}
                types={['veterinary_care']}
                onSelectResult={setClinic}
                searchResultName="displayName"
                textsearchAdditionalQuery={city?.text.text}
            />
        </Subbody>
    );

    return (
        <MedicalRecordsContainer>
            <Body>
                <Instructions>
                    <Fonts.Heading04>Search for previous clinic</Fonts.Heading04>
                    <Fonts.Body style={{ color: colors.GREY_2 }}>
                        We will reach out to your vet and request records for you.
                    </Fonts.Body>
                </Instructions>
                {renderCityAndClinicInputs()}
            </Body>
            <FooterButtons isMobile={isMobile}>
                <PrimaryButton
                    id="medical-records-button"
                    disabled={!isValidInfo}
                    color="periwinkle"
                    onClick={requestRecords}
                >
                    Next
                </PrimaryButton>
            </FooterButtons>
        </MedicalRecordsContainer>
    );
};

export default MedicalRecordsClinicRequest;

const MedicalRecordsContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 32px 24px 0 24px;
  gap: 8px;
  justify-content: space-between;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FooterButtons = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  position: ${({ isMobile }) => (isMobile ? 'sticky' : 'static')};
  bottom: 0px;
  background-color: ${colors.WHITE};
  padding: 16px 0 32px 0;
`;

const Instructions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Subbody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
